import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { leaveRequestsApi } from '@/services/api/endpoints/hr';
import type { LeaveRequest } from '@/types/hr';
import { formatDate, orDash } from '@/utils/format';
import { Days, Field } from './shared';

/**
 * Approving or refusing leave.
 *
 * <p>A refusal needs a reason, which is enforced here as well as on the server:
 * the button is disabled until one is typed, so the user is not made to submit
 * in order to find out.
 *
 * <p>Approving at the final stage writes attendance for the days it covers, and
 * the response reports any it could not — days the employee was already recorded
 * as having worked. Those are shown rather than swallowed, because they mean the
 * two records disagree and somebody has to settle it.
 */
export function LeaveDecisionDialog({
  request,
  stage,
  onClose,
  onDecided,
}: {
  request: LeaveRequest;
  stage: 'MANAGER' | 'HR';
  onClose: () => void;
  onDecided: () => void;
}) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();

  const [comment, setComment] = useState('');
  const [conflicts, setConflicts] = useState<string[] | null>(null);

  const download = useMutation({
    mutationFn: () => leaveRequestsApi.downloadDocument(request.id, request.documentFilename),
    onError: (error) => handleApiError(error),
  });

  const decide = useMutation({
    mutationFn: (approve: boolean) => {
      const payload = { approve, comment: comment.trim() === '' ? undefined : comment.trim() };
      return stage === 'MANAGER'
        ? leaveRequestsApi.managerDecision(request.id, payload)
        : leaveRequestsApi.hrDecision(request.id, payload);
    },
    onSuccess: (updated) => {
      const clashes = updated.attendanceConflicts ?? [];
      if (clashes.length > 0) {
        // Held open: the approval succeeded, but the user needs to know that
        // some days were left as they were.
        setConflicts(clashes);
        return;
      }
      toast.success(`${updated.requestNumber} ${updated.status === 'REJECTED' ? 'refused' : 'approved'}`);
      onDecided();
    },
    onError: (error) => handleApiError(error),
  });

  if (conflicts !== null) {
    return (
      <Modal open onClose={onDecided} title="Approved, with days left as they were">
        <div className="flex items-start gap-2 border border-line bg-surface p-3">
          <TriangleAlert
            className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--sms-warning)]"
            aria-hidden="true"
          />
          <p className="text-[13px] text-muted">
            {request.employeeName} was already recorded as having worked{' '}
            {conflicts.length === 1 ? 'this day' : 'these days'}, so attendance was not overwritten:{' '}
            <span className="text-ink">
              {conflicts.map((date) => formatDate(date)).join(', ')}
            </span>
            . The leave is approved and the days are charged — correct the register if the absence
            was real.
          </p>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={onDecided}>Understood</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={stage === 'MANAGER' ? 'Manager decision' : 'HR decision'}
    >
      <dl className="text-sm">
        <Field label="Request">{request.requestNumber}</Field>
        <Field label="Employee">{orDash(request.employeeName)}</Field>
        <Field label="Type">{orDash(request.leaveTypeName)}</Field>
        <Field label="Dates">
          {request.startDate === request.endDate
            ? formatDate(request.startDate)
            : `${formatDate(request.startDate)} → ${formatDate(request.endDate)}`}
          {request.halfDay && ' (half day)'}
        </Field>
        <Field label="Days charged">
          <Days value={request.days} />
        </Field>
        <Field label="Reason">{request.reason}</Field>
        <Field label="Evidence">
          {request.documentFilename === undefined ? (
            <span className="text-muted">None attached</span>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              title={request.documentFilename}
              loading={download.isPending}
              onClick={() => download.mutate()}
            >
              <span className="max-w-[12rem] truncate">{request.documentFilename}</span>
            </Button>
          )}
        </Field>
        {request.managerComment !== undefined && stage === 'HR' && (
          <Field label="Manager said">{request.managerComment}</Field>
        )}
        {request.balanceRemaining !== undefined && (
          <Field label="Balance if approved">
            <Days value={request.balanceRemaining} />
          </Field>
        )}
      </dl>

      <div className="mt-3">
        <TextField
          label="Comment"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Required when refusing"
        />
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="danger"
          disabled={comment.trim() === '' || decide.isPending}
          onClick={() => decide.mutate(false)}
        >
          Refuse
        </Button>
        <Button type="button" disabled={decide.isPending} onClick={() => decide.mutate(true)}>
          {decide.isPending ? 'Saving…' : 'Approve'}
        </Button>
      </div>
    </Modal>
  );
}
